import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import { Button, Icons, Modal, Select, SpinnerOverlay } from '@components/common';
import Dropzone from '@components/common/Dropzone';
import FilesList from '@components/common/FilesList';

import { useMutatePortalAttachFiles } from '@hooks/portal/files/useMutatePortalAttachFiles';
import { useQueryPortalFilesMilestones } from '@hooks/portal/files/useQueryPortalFilesMilestones';
import { useQueryPortalFilesUsers } from '@hooks/portal/files/useQueryPortalFilesUsers';

import { IAssetFile } from '@interfaces/assets/asset';
import { TNameTitleValueLabel } from '@interfaces/common/common';
import { IFileFormValues } from '@interfaces/components/form';
import { IUploadFilesDTO } from '@interfaces/portal/dto/foldersManagementDTO';
import { IPortalFolder } from '@interfaces/portal/folders';

import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { HUNDRED_PERCENT_LOADING } from '@constants/common';
import { selectFolderModalTitle } from '../MoveFolderForm/constants';
import {
	acceptedFileTypes,
	fileErrors,
	MAX_BYTES,
	supportsText,
	uploadFileFormValues,
} from './constants';

import SelectFolderModal from '../SelectFolderModal';
import { schema } from './schema';

import styles from './UploadFileForm.module.scss';

interface IUploadFileFormProps {
	closeUploadFileForm: () => void;
	selectedFolder: IPortalFolder;
	projectId: string;
	uploadFiles: (data: IUploadFilesDTO) => Promise<void>;
}

const UploadFileForm = ({
	closeUploadFileForm,
	selectedFolder,
	projectId,
	uploadFiles,
}: IUploadFileFormProps) => {
	const [isSelectFolderModal, setIsSelectFolderModal] = useState(false);
	const [uploadedFiles, setUploadedFiles] = useState<Array<IAssetFile>>([]);

	const openSelectFolderModal = () => setIsSelectFolderModal(true);

	const closeSelectFolderModal = () => setIsSelectFolderModal(false);

	const {
		handleSubmit,
		formState: { errors },
		setValue,
		watch,
		control,
	} = useForm<IFileFormValues>({
		resolver: yupResolver(schema),
		mode: EValidationMode.onSubmit,
		reValidateMode: ERevalidationMode.onChange,
		defaultValues: {
			folder: selectedFolder,
		},
	});

	const { portalFilesUsers, isLoadingPortalFilesUsers } = useQueryPortalFilesUsers(projectId);
	const { portalFilesMilestones, isLoadingPortalFilesMilestones } = useQueryPortalFilesMilestones(projectId);

	const {
		createTaskUploadUrlAttachment,
		progress,
	} = useMutatePortalAttachFiles({ setUploadedFiles });

	const handleRemoveFile = (fileId: string) => {
		setUploadedFiles(prevState => prevState.filter((file: IAssetFile) => file.id !== fileId));
	};

	const isUploading = progress !== undefined ? progress !== HUNDRED_PERCENT_LOADING : false;

	const folder = watch(uploadFileFormValues.folder);
	const usersValue = watch(uploadFileFormValues.users);
	const milestonesValue = watch(uploadFileFormValues.milestones);

	const selectFolder = (folder: IPortalFolder) => {
		setValue(uploadFileFormValues.folder, folder);

		closeSelectFolderModal();
	};

	const onChangeUsers = (options: TNameTitleValueLabel[]) => {
		setValue(uploadFileFormValues.users, options);
	};

	const onChangeMilestones = (options: TNameTitleValueLabel[]) => {
		setValue(uploadFileFormValues.milestones, options);
	};

	const onProceed = (values: IFileFormValues) => {
		void uploadFiles({
			importIds: uploadedFiles.map(file => file.id),
			projectId,
			folderId: values.folder.id,
			folderName: values.folder.name,
			usersToNotify: values.users?.map(user => user.value),
			milestoneIds: values.milestones?.map(milestone => milestone.value),
		});

		closeUploadFileForm();
	};

	return (
		<>
			{(isLoadingPortalFilesUsers || isLoadingPortalFilesMilestones || isUploading) && <SpinnerOverlay />}
			<div className={styles.uploadFileContainer}>
				<div className={styles.folderInput}>
					<div className={styles.label}>
						Folder
					</div>
					<div className={styles.folderName}>
						{folder.hasContent ? Icons.folderIcon() : Icons.folderEmpty()}
						{folder.name || ''}
					</div>
					<div
						className={styles.selectFolderButton}
						onClick={openSelectFolderModal}
					>
						{selectFolderModalTitle}
					</div>
				</div>
				<div className={styles.selectMilestones}>
					<Select
						closeMenuOnSelect={false}
						control={control}
						handleChange={onChangeMilestones}
						isClearable={false}
						isError={!!errors.milestones}
						isMulti
						label='Milestone'
						multiselectText='milestones selected'
						name='milestones'
						options={portalFilesMilestones}
						placeholder='Choose milestone'
						value={milestonesValue}
					/>
				</div>
				<div className={styles.selectUsers}>
					<Select
						closeMenuOnSelect={false}
						control={control}
						handleChange={onChangeUsers}
						isClearable={false}
						isError={!!errors.users}
						isMulti
						label='People to notify'
						multiselectText='people selected'
						name='users'
						options={portalFilesUsers}
						placeholder='Choose people to notify'
						value={usersValue}
					/>
				</div>
				<div className={styles.label}>
					Attachments
				</div>
				<div className={styles.dropzone}>
					<Dropzone
						acceptedFileTypes={acceptedFileTypes}
						createUploadUrl={createTaskUploadUrlAttachment as any}
						errors={fileErrors}
						isMultipleFiles
						isUploadedFilesInfo={false}
						maxSize={MAX_BYTES}
						supportsText={supportsText}
						uploadIcon={Icons.uploadFile}
						uploadedIcon={Icons.uploadedFile}
					/>
				</div>
				<div className={styles.filesList}>
					<FilesList
						files={uploadedFiles}
						handleRemoveFile={handleRemoveFile}
					/>
				</div>
			</div>
			<div className={styles.buttons}>
				<Button
					onClick={closeUploadFileForm}
					text='Cancel'
					withoutBackground
				/>
				<Button
					onClick={handleSubmit(onProceed)}
					text='Proceed'
				/>
			</div>
			<Modal
				isOpen={isSelectFolderModal}
				isScrollableContent
				maxHeight='600px'
				onClose={closeSelectFolderModal}
				title={selectFolderModalTitle}
				width='400px'
			>
				<SelectFolderModal
					projectId={projectId}
					selectFolder={selectFolder}
				/>
			</Modal>
		</>
	);
};

export default UploadFileForm;
